import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { inject, Injectable, Injector, signal, WritableSignal } from '@angular/core';
import { Element } from '../../equipment/models/elements/element';
import { DetailedElementWidgetComponent } from '../planogram-edit/components/detailed-element-widget/detailed-element-widget.component';
import {
	DETAILED_ELEMENT_WIDGET_DATA,
	DETAILED_ELEMENT_WIDGET_REF,
	DetailedElementWidgetData,
	DetailedElementWidgetRef
} from '../planogram-edit/components/detailed-element-widget/detailed-element-widget.tokens';
import { IPlanogram } from '../models/planogram';
import { OverlayZIndexService } from './overlay-z-index.service';
import { clampOverlayToViewport } from './overlay-viewport.util';

export interface DetailedElementWidgetOverlayOpenParams {
	overlay: Overlay;
	parentInjector: Injector;
	hostContainer?: HTMLElement;
	boundingRect: DOMRect;
	element: Element;
	planogram: IPlanogram;
	afterClose?: () => void;
}

interface OpenedDetailedElement {
	overlay: OverlayRef;
	element: Element;
}

@Injectable()
export class DetailedElementWidgetOverlayService {

	private readonly opened: WritableSignal<Array<OpenedDetailedElement>> = signal([]);
	private _windowResizeAttached: boolean = false;
	private _windowResizeFrame: number | null = null;
	private readonly _onWindowResize = (): void => {
		if (this._windowResizeFrame != null) cancelAnimationFrame(this._windowResizeFrame);

		this._windowResizeFrame = requestAnimationFrame(() => {
			this._windowResizeFrame = null;
			this.opened().forEach((item: OpenedDetailedElement) => {
				clampOverlayToViewport(item.overlay.overlayElement as HTMLElement);
			});
		});
	};

    private readonly overlayZIndexService: OverlayZIndexService = inject(OverlayZIndexService);

	public isOpened(element: Element): boolean {
		return this.opened().some((item: OpenedDetailedElement) => item.element === element);
	}

	public hasOpened(): boolean {
		return this.opened().length > 0;
	}

	public hasOpenedForPlanogram(planogram: IPlanogram): boolean {
		const elements: Set<Element> = new Set(planogram.stand.allElements);

		return this.opened().some((item: OpenedDetailedElement) => elements.has(item.element));
	}

	public focus(element: Element): void {
		const item: OpenedDetailedElement | undefined = this.opened()
			.find((openedItem: OpenedDetailedElement) => openedItem.element === element);

		if (item) this.overlayZIndexService.setZIndex(item.overlay);
	}

	public open(params: DetailedElementWidgetOverlayOpenParams): OverlayRef {
		const overlayRef: OverlayRef = params.overlay.create({
			positionStrategy: params.overlay.position().global(),
			hasBackdrop: false,
			panelClass: 'detailed-element-widget-overlay',
			scrollStrategy: params.overlay.scrollStrategies.noop()
		});

		overlayRef.overlayElement.style.left = `${params.boundingRect.left}px`;
		overlayRef.overlayElement.style.top = `${params.boundingRect.top}px`;

		const widgetData: DetailedElementWidgetData = {
			element: params.element,
			planogram: params.planogram,
			overlayRef: overlayRef,
			boundingRect: params.boundingRect
		};
		const widgetRef: DetailedElementWidgetRef = {
			focus: () => this.overlayZIndexService.setZIndex(overlayRef),
			close: () => {
				this.close(overlayRef);
				params.afterClose?.();
			}
		};
		const injector: Injector = Injector.create({
			parent: params.parentInjector,
			providers: [
				{ provide: DETAILED_ELEMENT_WIDGET_DATA, useValue: widgetData },
				{ provide: DETAILED_ELEMENT_WIDGET_REF, useValue: widgetRef }
			]
		});
		const portal: ComponentPortal<DetailedElementWidgetComponent> = new ComponentPortal(DetailedElementWidgetComponent, null, injector);
		overlayRef.attach(portal);

		params.hostContainer.appendChild(overlayRef.overlayElement);

		this.opened.update((items: Array<OpenedDetailedElement>) => [...items, { overlay: overlayRef, element: params.element }]);
		this.overlayZIndexService.setZIndex(overlayRef);
		this.attachWindowResizeListener();

		return overlayRef;
	}

	public close(overlayRef: OverlayRef): void {
		this.opened.update((items: Array<OpenedDetailedElement>) =>
			items.filter((item: OpenedDetailedElement) => item.overlay !== overlayRef)
		);
		overlayRef.dispose();
		this.detachWindowResizeListenerIfEmpty();
	}

	public closeAll(): void {
		if (!this.hasOpened()) return;

		this.opened().forEach((item: OpenedDetailedElement) => {
			item.overlay.dispose();
		});
		this.opened.set([]);
		this.detachWindowResizeListenerIfEmpty();
	}

	public closeAllForPlanogram(planogram: IPlanogram): void {
		const elements: Set<Element> = new Set(planogram.stand.allElements);
		const toClose: Array<OpenedDetailedElement> = this.opened()
			.filter((item: OpenedDetailedElement) => elements.has(item.element));

		if (!toClose.length) return;

		toClose.forEach((item: OpenedDetailedElement) => item.overlay.dispose());
		this.opened.update((items: Array<OpenedDetailedElement>) =>
			items.filter((item: OpenedDetailedElement) => !elements.has(item.element))
		);
		this.detachWindowResizeListenerIfEmpty();
	}

	private attachWindowResizeListener(): void {
		if (this._windowResizeAttached) return;

		window.addEventListener('resize', this._onWindowResize);
		this._windowResizeAttached = true;
	}

	private detachWindowResizeListenerIfEmpty(): void {
		if (this.hasOpened() || !this._windowResizeAttached) return;

		window.removeEventListener('resize', this._onWindowResize);
		this._windowResizeAttached = false;

		if (this._windowResizeFrame != null) {
			cancelAnimationFrame(this._windowResizeFrame);
			this._windowResizeFrame = null;
		}
	}
}
